/**
 * Date utility functions for handling DD-MM-YYYY format dates
 */

/**
 * Parse a DD-MM-YYYY date string into a Date object
 * @param dateString - Date string in DD-MM-YYYY format
 * @returns Date object or null if invalid
 */
export function parseDDMMYYYY(dateString: string): Date | null {
  if (!dateString) return null;
  
  // Check if it's in DD-MM-YYYY format
  const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
  if (!ddmmyyyyRegex.test(dateString)) {
    return null;
  }
  
  const [day, month, year] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Validate that the date is correct (no overflow like 32/13/2023)
  if (date.getFullYear() !== year || 
      date.getMonth() !== month - 1 || 
      date.getDate() !== day) {
    return null;
  }
  
  return date;
}

/**
 * Format a Date object to DD-MM-YYYY string
 * @param date - Date object to format
 * @returns Date string in DD-MM-YYYY format
 */
export function formatToDDMMYYYY(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();
  
  return `${day}-${month}-${year}`;
}

/**
 * Format a DD-MM-YYYY date string for display
 * @param dateString - Date string in DD-MM-YYYY format
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted date string or 'Invalid Date' if parsing fails
 */
export function formatDateForDisplay(dateString: string, locale: string = 'en-US'): string {
  const date = parseDDMMYYYY(dateString);
  
  if (!date) {
    return 'Invalid Date';
  }
  
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Check if a DD-MM-YYYY date string is valid
 * @param dateString - Date string to validate
 * @returns True if valid, false otherwise
 */
export function isValidDDMMYYYY(dateString: string): boolean {
  return parseDDMMYYYY(dateString) !== null;
}

/**
 * Convert YYYY-MM-DD to DD-MM-YYYY format
 * @param yyyymmdd - Date string in YYYY-MM-DD format
 * @returns Date string in DD-MM-YYYY format
 */
export function convertYYYYMMDDtoDDMMYYYY(yyyymmdd: string): string {
  if (!yyyymmdd) return '';
  
  const yyyymmddRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!yyyymmddRegex.test(yyyymmdd)) {
    return yyyymmdd; // Return as-is if not in expected format
  }
  
  const [year, month, day] = yyyymmdd.split('-');
  return `${day}-${month}-${year}`;
}

/**
 * Convert DD-MM-YYYY to YYYY-MM-DD format
 * @param ddmmyyyy - Date string in DD-MM-YYYY format
 * @returns Date string in YYYY-MM-DD format
 */
export function convertDDMMYYYYtoYYYYMMDD(ddmmyyyy: string): string {
  if (!ddmmyyyy) return '';
  
  const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
  if (!ddmmyyyyRegex.test(ddmmyyyy)) {
    return ddmmyyyy; // Return as-is if not in expected format
  }
  
  const [day, month, year] = ddmmyyyy.split('-');
  return `${year}-${month}-${day}`;
}
